import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';

interface TermsProps {
  currentUrl: string;
  lastUpdated: string;
}

const FileTextIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CartIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const TruckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const AlertIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ScaleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="3" x2="12" y2="21" />
    <path d="M5 12H2l3.5-7L9 12H5z" />
    <path d="M19 12h-3l3.5-7L23 12h-4z" />
    <line x1="5" y1="3" x2="19" y2="3" />
  </svg>
);

const MapPinIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export default function Terms({ currentUrl, lastUpdated }: TermsProps) {
  const pageTitle = 'Términos y Condiciones | Abasto Virtual Córdoba';
  const pageDescription = 'Conocé los términos y condiciones de uso de Abasto Virtual. Condiciones generales para la compra de frutas, verduras y productos frescos en Córdoba.';

  return (
    <MainLayout>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="términos condiciones, condiciones uso, abasto virtual córdoba, compra online frutas verduras" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="/appicons/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="/appicons/og-image.jpg" />
        <link rel="canonical" href={currentUrl} />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-3xl px-4">
          {/* Header */}
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100">
              <FileTextIcon className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Términos y Condiciones
            </h1>
            <p className="text-lg text-gray-500">
              Condiciones generales de uso de Abasto Virtual
            </p>
          </div>

          {/* Intro card */}
          <div className="mb-10 rounded-2xl border-l-4 border-blue-500 bg-blue-50 p-6">
            <p className="text-base leading-relaxed text-blue-900">
              Al acceder y utilizar <strong>abastovirtual.com</strong>, aceptás estos términos y condiciones.
              Te recomendamos leerlos detenidamente antes de realizar cualquier compra.
            </p>
          </div>

          {/* Content sections */}
          <div className="space-y-10">
            {/* General conditions */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                  <FileTextIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Condiciones Generales</h2>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Abasto Virtual es una plataforma de comercio electrónico dedicada a la venta de frutas, verduras y productos frescos en la ciudad de Córdoba, Argentina.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigencia desde su publicación en el sitio.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>El uso continuado del sitio después de cambios en los términos constituye aceptación de los mismos.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* User account */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100">
                  <UserIcon className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Cuenta de Usuario</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Registro</h4>
                  <p className="text-gray-600">
                    Para realizar compras, debés crear una cuenta proporcionando información veraz y actualizada.
                    Sos responsable de mantener la confidencialidad de tu contraseña y de todas las actividades que ocurran bajo tu cuenta.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Responsabilidad</h4>
                  <p className="text-gray-600">
                    Debés notificarnos inmediatamente si detectás cualquier uso no autorizado de tu cuenta.
                    No seremos responsables por pérdidas derivadas del uso no autorizado de tu cuenta.
                  </p>
                </div>
              </div>
            </section>

            {/* Orders & payments */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                  <CartIcon className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Pedidos y Pagos</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Precios</h4>
                  <p className="text-gray-600">
                    Los precios publicados están expresados en pesos argentinos (ARS) e incluyen IVA cuando corresponda.
                    Nos reservamos el derecho de modificar los precios sin previo aviso. El precio aplicable será el vigente al momento de confirmar tu pedido.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Métodos de Pago</h4>
                  <p className="text-gray-600">
                    Aceptamos tarjetas de crédito, débito, transferencia bancaria y pago en efectivo contra entrega.
                    Los pagos electrónicos son procesados de forma segura a través de proveedores autorizados.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Productos frescos</h4>
                  <p className="text-gray-600">
                    Dado que trabajamos con productos frescos, el peso final puede variar levemente respecto al solicitado.
                    Esto es inherente a la naturaleza de los productos y se refleja en el monto final facturado.
                  </p>
                </div>
              </div>
            </section>

            {/* Delivery */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                  <TruckIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Entrega</h2>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Las entregas se realizan dentro de las zonas de cobertura habilitadas en Córdoba.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Los horarios y días de entrega están sujetos a disponibilidad según tu zona.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Cada zona tiene un monto mínimo de compra y un costo de envío asociado.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>Es responsabilidad del cliente proporcionar una dirección correcta y estar disponible para recibir el pedido en el horario acordado.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    <span>En caso de no poder entregar por causas ajenas a nosotros, se podrá reprogramar la entrega o cancelar el pedido.</span>
                  </li>
                </ul>
              </div>
              <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-5 flex items-start gap-3">
                <TruckIcon className="h-5 w-5 flex-shrink-0 text-green-600 mt-0.5" />
                <p className="text-sm text-green-800">
                  Consultá el costo de envío y pedido mínimo para tu dirección en nuestra{' '}
                  <a href="/#zonas" className="font-semibold text-green-700 underline hover:text-green-900">sección de zonas de entrega</a>.
                </p>
              </div>
            </section>

            {/* Intellectual property */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100">
                  <LockIcon className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Propiedad Intelectual</h2>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <p className="text-gray-600">
                  Todo el contenido del sitio web, incluyendo pero no limitado a textos, imágenes, logotipos, diseños y software,
                  es propiedad de Abasto Virtual o de sus licenciantes y está protegido por las leyes de propiedad intelectual de la República Argentina.
                  Queda prohibida su reproducción, distribución o modificación sin autorización previa por escrito.
                </p>
              </div>
            </section>

            {/* Limitation of liability */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                  <AlertIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Limitación de Responsabilidad</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <p className="text-gray-600">
                    Abasto Virtual no será responsable por daños indirectos, incidentales o consecuentes que pudieran surgir
                    del uso o la imposibilidad de uso del sitio web o de los productos adquiridos.
                  </p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <p className="text-sm text-amber-800">
                    <strong>Productos frescos:</strong> Dada la naturaleza perecedera de nuestros productos,
                    no nos hacemos responsables por deterioro causado por almacenamiento inadecuado posterior a la entrega.
                    Consultá nuestra <a href="/politica-de-devolucion" className="text-green-600 font-semibold hover:underline">política de devolución</a> para más información.
                  </p>
                </div>
              </div>
            </section>

            {/* Applicable law */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100">
                  <ScaleIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Ley Aplicable</h2>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <p className="text-gray-600">
                  Estos términos y condiciones se rigen por las leyes de la República Argentina.
                  Cualquier controversia derivada del uso del sitio o de estos términos será sometida a la jurisdicción
                  de los tribunales ordinarios de la ciudad de Córdoba, Provincia de Córdoba, Argentina.
                </p>
              </div>
            </section>

            {/* Footer info */}
            <div className="space-y-4">
              <div className="rounded-xl bg-gray-100 p-5">
                <div className="mb-2 flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-700">Información de Contacto</h4>
                </div>
                <p className="text-sm text-gray-600">
                  De La Semillería 1731 - Ampl. Palmar, Córdoba, 5012, Argentina
                </p>
              </div>

              <div className="rounded-xl bg-gray-50 py-4 text-center">
                <p className="text-sm text-gray-500">
                  Última actualización: {lastUpdated}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Términos y Condiciones',
            description: 'Términos y condiciones de uso de Abasto Virtual',
            url: currentUrl,
            mainEntity: {
              '@type': 'TermsOfService',
              name: 'Términos y Condiciones de Abasto Virtual',
              publisher: {
                '@type': 'Organization',
                name: 'Abasto Virtual',
                url: 'https://www.abastovirtual.com',
              },
            },
          }),
        }}
      />
    </MainLayout>
  );
}
